import { getApiErrorMessage, isEmailAlreadyExistsError } from './helpers';

export type AuthErrorCode =
  | 'invalidCredentials'
  | 'emailAlreadyExists'
  | 'emailRequired'
  | 'passwordRequired'
  | 'nameRequired'
  | 'invalidEmailFormat'
  | 'phoneRequired'
  | 'invalidPhoneFormat'
  | 'passwordTooShort'
  | 'passwordsDoNotMatch'
  | 'referralSourceRequired'
  | 'referralSourceTooLong'
  | 'verificationEmailFailed'
  | 'invalidInput'
  | 'network'
  | 'timeout'
  | 'server'
  | 'unknown';

const resolveTransportErrorCode = (error: any): AuthErrorCode | null => {
  if (error?.response) return null;

  const code = error?.code;
  if (
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    /timeout/i.test(String(error?.message ?? ''))
  ) {
    return 'timeout';
  }
  if (code === 'ERR_NETWORK' || error?.isAxiosError || error?.request) {
    return 'network';
  }

  return 'unknown';
};

// Every rejected login collapses to one indistinguishable code: branching on the
// backend message would let an attacker enumerate which addresses have accounts.
export const resolveLoginErrorCode = (error: any): AuthErrorCode => {
  const transport = resolveTransportErrorCode(error);
  if (transport) return transport;

  const status = error?.response?.status;

  if (status === 429 || status >= 500) return 'server';
  if (status >= 400) return 'invalidCredentials';

  return 'unknown';
};

export const resolveSignUpErrorCode = (error: any): AuthErrorCode => {
  const transport = resolveTransportErrorCode(error);
  if (transport) return transport;

  const status = error?.response?.status;
  const message = getApiErrorMessage(error).toLowerCase();

  if (isEmailAlreadyExistsError(error)) return 'emailAlreadyExists';
  if (/error sending email|sendgrid/.test(message)) {
    return 'verificationEmailFailed';
  }
  if (/email not valid|invalid email/.test(message)) return 'invalidEmailFormat';
  if (/password length at least|at least 8|too short/.test(message)) {
    return 'passwordTooShort';
  }
  if (/do not match|confirm password not valid|mismatch/.test(message)) {
    return 'passwordsDoNotMatch';
  }
  if (/referralsource too long|referral.*too long/.test(message)) {
    return 'referralSourceTooLong';
  }
  if (/referralsource is required|referral.*required/.test(message)) {
    return 'referralSourceRequired';
  }
  if (/firstname is required|lastname is required/.test(message)) {
    return 'nameRequired';
  }
  if (/phone is required/.test(message)) return 'phoneRequired';
  if (/phone not valid|invalid phone/.test(message)) return 'invalidPhoneFormat';
  if (/email is required/.test(message)) return 'emailRequired';
  if (/password is required/.test(message)) return 'passwordRequired';
  if (status >= 500) return 'server';
  if (status === 400) return 'invalidInput';

  return 'unknown';
};
